import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Checkbox } from 'components/v2/Checkbox'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'

import { SelectSegmentsForm } from 'components/SelectSegments'
import { Label } from 'components/v2/Label'
import styled from 'styled-components'
import SelectCities from 'components/SelectCities'
import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { ICreateRuleForm, IFormSegment, IPrevSegmentSettings, TReadOnlyItem } from '../CreateRule/types'
import { IRuleCitySegmentsProps } from './types'
import { GTLoyaltyCampaignPlace } from 'gql/types.generated'
import { useAllSegmentsByCities } from 'features/Segments/hooks'
import { mapSegmentSuggestionsToFormSegments } from 'features/Segments/utils'

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`

const RuleCitySegments = ({ isEditView, isCampaignStarted, companyId }: IRuleCitySegmentsProps): ReactElement => {
  const { values, setFieldValue, touched, errors, setFieldTouched, initialValues } = useFormikContext<ICreateRuleForm>()
  const [segmentSelectValues, setSegmentSelectValues] = useState({
    segmentOrigins: [] as TReadOnlyItem<GTLoyaltyCampaignPlace>[] | GTLoyaltyCampaignPlace[],
    segmentDestinations: [] as TReadOnlyItem<GTLoyaltyCampaignPlace>[] | GTLoyaltyCampaignPlace[]
  })
  const { segmentSuggestions } = useAllSegmentsByCities({
    companyId,
    cityOrigins: values.cityOrigins,
    cityDestinations: values.cityDestinations,
    isBothWays: values.isReturn
  })
  const segmentsOptions = mapSegmentSuggestionsToFormSegments(segmentSuggestions) as IFormSegment[]

  const [prevSegmentsSettings, setPrevSegmentsValues] = useState<IPrevSegmentSettings>({
    cityOrigins: [],
    cityDestinations: [],
    segmentsOrigins: [],
    segmentsDestinations: []
  })
  const handleCheckboxChange = (value: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('allCitiesAndSegments', value.target.checked)
    setFieldValue('allSegments', value.target.checked)
    if (value.target.checked) {
      setPrevSegmentsValues({
        cityOrigins: values.cityOrigins,
        cityDestinations: values.cityDestinations,
        segmentsOrigins: segmentSelectValues.segmentOrigins,
        segmentsDestinations: segmentSelectValues.segmentDestinations
      })
      setFieldValue('cityOrigins', [])
      setFieldValue('cityDestinations', [])
      setSegmentSelectValues({
        segmentDestinations: [],
        segmentOrigins: []
      })
    }
    if (!value.target.checked) {
      setFieldValue('cityOrigins', prevSegmentsSettings.cityOrigins)
      setFieldValue('cityDestinations', prevSegmentsSettings.cityDestinations)
      setSegmentSelectValues({
        segmentDestinations: prevSegmentsSettings.segmentsDestinations,
        segmentOrigins: prevSegmentsSettings.segmentsOrigins
      })
    }
  }

  const handleAllSegmentsCheckboxChange = (value: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('allSegments', value.target.checked)
    if (value.target.checked) {
      setPrevSegmentsValues((prev) => ({
        ...prev,
        segmentsOrigins: segmentSelectValues.segmentOrigins,
        segmentsDestinations: segmentSelectValues.segmentDestinations
      }))
      setSegmentSelectValues({
        segmentDestinations: [],
        segmentOrigins: []
      })
    }
    if (!value.target.checked) {
      setSegmentSelectValues({
        segmentDestinations: prevSegmentsSettings.segmentsDestinations,
        segmentOrigins: prevSegmentsSettings.segmentsOrigins
      })
    }
  }

  const handleCitiesChange = (
    cities: ICreateRuleForm['cityOrigins'] | ICreateRuleForm['cityDestinations'],
    type: 'origin' | 'destination'
  ): void => {
    if (type === 'origin') {
      setFieldValue('cityOrigins', cities)
    } else {
      setFieldValue('cityDestinations', cities)
    }
  }

  useEffect(() => {
    setSegmentSelectValues({
      segmentOrigins: values.segments.reduce((result, current) => {
        const segmentOrigin = current['origin']
        const added = result.find(({ id }) => id === segmentOrigin.id)
        if (!added) {
          result.push({ ...segmentOrigin, readOnly: true })
        }
        return result
      }, [] as TReadOnlyItem<GTLoyaltyCampaignPlace>[]),
      segmentDestinations: values.segments.reduce((result, current) => {
        const segmentDestination = current['destination']
        const added = result.find(({ id }) => id === segmentDestination.id)
        if (!added) {
          result.push({ ...segmentDestination, readOnly: true })
        }
        return result
      }, [] as TReadOnlyItem<GTLoyaltyCampaignPlace>[])
    })
  }, [])

  const updateSegments = (): void => {
    const newSegments = [...values.segments]
    values.segments.forEach((segment) => {
      const returnSegment = segmentsOptions.find(
        (item) => item.origin.id === segment.destination.id && item.destination.id === segment.origin.id
      )
      const isAdded = newSegments.find((item) => item.id === returnSegment?.id)
      if (returnSegment) {
        if (values.isReturn && !isAdded) {
          newSegments.push(returnSegment)
        }
        if (!values.isReturn && isAdded) {
          newSegments.splice(newSegments.indexOf(returnSegment), 1)
        }
      }
    })
    setFieldValue('segments', newSegments)
  }

  useEffect(() => {
    if (touched.isReturn) {
      updateSegments()
    }
  }, [segmentsOptions.length, values.isReturn, touched.isReturn])

  const handleBothWaysChange = (value: boolean): void => {
    if (initialValues.isReturn && isEditView && isCampaignStarted) {
      return
    }
    setFieldValue('isReturn', value)
    setFieldTouched('isReturn', true)
  }

  const validateAllSegmentsCheckboxDisabled = (): boolean => {
    if (isEditView) {
      if (isCampaignStarted && initialValues.allSegments) {
        return true
      }
    }

    if (values.allCitiesAndSegments) {
      return true
    }

    return false
  }
  const isAllSegmentsCheckboxDisabled = validateAllSegmentsCheckboxDisabled()
  const isAllCitiesAndSegmentsCheckboxDisabled = isEditView && isCampaignStarted && initialValues.allCitiesAndSegments
  return (
    <>
      <Row>
        <Column>
          <CheckboxContainer>
            <Checkbox
              id="loyalty.campaing.rule.new.selectAll.id"
              checked={values.allCitiesAndSegments}
              onChange={handleCheckboxChange}
              disabled={isAllCitiesAndSegmentsCheckboxDisabled}
            />
            <FormattedMessage
              id="loyalty.campaing.rule.new.selectAll.label"
              defaultMessage="Select all cities and segments"
            />
          </CheckboxContainer>
        </Column>
      </Row>
      <div>
        <Label>
          <FormattedMessage id="loyalty.campaing.rule.new.cities.label" defaultMessage="Cities" />
        </Label>
      </div>
      <SelectCities
        id="cities"
        disabled={values.allCitiesAndSegments}
        isOnlyAdd={Boolean(isEditView && isCampaignStarted)}
        originValue={values.cityOrigins}
        destinationValue={values.cityDestinations}
        // @ts-expect-error FIXME
        onChange={handleCitiesChange}
        touched={touched}
        errors={errors}
        isReturn={values.isReturn}
        handleReturn={handleBothWaysChange}
      />
      <Row>
        <Column>
          <CheckboxContainer>
            <Checkbox
              id="loyalty.campaing.rule.new.selectAllSegments.id"
              checked={values.allSegments}
              onChange={handleAllSegmentsCheckboxChange}
              disabled={isAllSegmentsCheckboxDisabled}
            />
            <FormattedMessage
              id="loyalty.campaing.rule.new.selectAllSegments.label"
              defaultMessage="Select all segments"
            />
          </CheckboxContainer>
        </Column>
      </Row>
      <div>
        <Label>
          <FormattedMessage id="loyalty.campaing.rule.new.segments.label" defaultMessage="Segments" />
        </Label>
      </div>
      <Row>
        <Column>
          <SelectSegmentsForm
            segments={segmentsOptions ?? []}
            isOnlyAdd={Boolean(isEditView && isCampaignStarted)}
            disabled={values.allCitiesAndSegments || values.allSegments}
            companyId={companyId}
            values={values}
            //@ts-expect-error FIXME
            setFieldValue={(): void => undefined}
            isReturn={values.isReturn}
            segmentsOrigins={segmentSelectValues.segmentOrigins}
            segmentsDestinations={segmentSelectValues.segmentDestinations}
            setSegmentsOrigins={(segmentOrigins): void =>
              setSegmentSelectValues((prev) => ({ ...prev, segmentOrigins }))
            }
            setSegmentsDestinations={(segmentDestinations): void =>
              setSegmentSelectValues((prev) => ({ ...prev, segmentDestinations }))
            }
            setSegments={(segments: ICreateRuleForm['segments']): void => {
              setFieldValue('segments', segments)
            }}
            touched={touched}
            errors={errors}
          />
        </Column>
      </Row>
    </>
  )
}

export default RuleCitySegments
